import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Tabs, Tab, Backdrop } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Login from './Login';
import Register from './Register';
import VerifyEmail from './VerifyEmail';
import ForgotPassword from './ForgotPassword';

const VIEW = {
  LOGIN: 'login',
  REGISTER: 'register',
  VERIFY_EMAIL: 'verify_email',
  FORGOT_PASSWORD: 'forgot_password',
};

export default function AuthModal({ open, onClose, defaultTab = 0 }) {
  const [view, setView] = useState(defaultTab === 1 ? VIEW.REGISTER : VIEW.LOGIN);
  const [pendingEmail, setPendingEmail] = useState('');

  const handleClose = () => {
    setView(VIEW.LOGIN);
    setPendingEmail('');
    onClose();
  };

  // Called by Register when OTP is sent
  const handleEmailSent = (email) => {
    setPendingEmail(email);
    setView(VIEW.VERIFY_EMAIL);
  };

  const showTabs = view === VIEW.LOGIN || view === VIEW.REGISTER;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17,24,39,1) 0%, rgba(15,15,18,1) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          zIndex: 1,
          '&:hover': {
            background: 'rgba(229,64,4,0.1)',
            color: '#F6911D',
            transition: 'all 0.3s ease',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {/* Tabs */}
        {showTabs && (
          <div className="border-b border-white/10">
            <Tabs
              value={view === VIEW.LOGIN ? 0 : 1}
              onChange={(_, v) => setView(v === 0 ? VIEW.LOGIN : VIEW.REGISTER)}
              centered
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: '60px',
                  '&:hover': { color: 'rgba(246,145,29,0.8)' },
                },
                '& .Mui-selected': { color: '#F6911D !important' },
                '& .MuiTabs-indicator': {
                  background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                  height: '3px',
                },
              }}
            >
              <Tab label="Login" />
              <Tab label="Register" />
            </Tabs>
          </div>
        )}

        <div className="p-6 h-full overflow-y-auto">
          {view === VIEW.LOGIN && (
            <Login
              onClose={handleClose}
              onSwitchToRegister={() => setView(VIEW.REGISTER)}
              onForgotPassword={() => setView(VIEW.FORGOT_PASSWORD)}
              onUnverifiedEmail={(email) => {
                setPendingEmail(email);
                setView(VIEW.VERIFY_EMAIL);
              }}
            />
          )}
          {view === VIEW.REGISTER && (
            <Register
              onClose={handleClose}
              onSwitchToLogin={() => setView(VIEW.LOGIN)}
              onEmailSent={handleEmailSent}
            />
          )}
          {view === VIEW.VERIFY_EMAIL && <VerifyEmail email={pendingEmail} onClose={handleClose} />}
          {view === VIEW.FORGOT_PASSWORD && (
            <ForgotPassword onBackToLogin={() => setView(VIEW.LOGIN)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
